import React, { useCallback, useContext, useState } from 'react';
import styled from 'styled-components';

import PeriodSelector from 'components/Board/PeriodSelector';
import DateInputContext from 'contexts/DateInputContext';
import Typography from 'kingpin/atoms/Typography';

import CalendarInput from './CalendarInput';
import { MAX_WIDTH_NON_CURRENT_DATE_RANGE } from './constants';
import { useLeftText, useRightText } from './index';
import RelativeInput from './RelativeInput';

type Tab = 'relative' | 'calendar';

const TABS: { key: Tab; label: string }[] = [
  { key: 'relative', label: 'Relative' },
  { key: 'calendar', label: 'Calendar' },
];

// No taller than the room its dialog has on the side it opens (see
// InlineDialog), less the dialog's border, so it never runs off the screen
const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  max-width: ${MAX_WIDTH_NON_CURRENT_DATE_RANGE}px;
  max-height: calc(var(--inline-dialog-available-height, 100vh) - 2px);
`;

// Under the range and tabs, which stay in view: scrolls when it's cut short
const Body = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
`;

const Header = styled.div`
  display: flex;
  flex-shrink: 0;
  flex-direction: column;
  gap: 8px;
  padding: 12px 12px 0;
`;

const Tabs = styled.div`
  display: flex;
  padding: 2px;
  border-radius: 8px;
  background-color: var(--color-bg-input);
`;

const TabButton = styled.button<{ $isSelected: boolean }>`
  flex: 1;
  height: 24px;
  padding: 0 12px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  background-color: ${(props) =>
    props.$isSelected ? 'var(--color-bg-primary)' : 'transparent'};
  box-shadow: ${(props) => (props.$isSelected ? 'var(--shadow-sm)' : 'none')};
`;

const BonusPeriod = styled.div`
  padding: 12px;
`;

// The range the panel shows first: the calendar for a custom range
const useInitialTab = (): Tab => {
  const { relativeDateRange, advancedRelativeDateRange } =
    useContext(DateInputContext);
  return relativeDateRange || advancedRelativeDateRange
    ? 'relative'
    : 'calendar';
};

// Every date picker option in one panel, for a picker opened from an icon
// rather than DateInput's two-part pill: the range it shows, then Relative
// (rolling and advanced windows, compare by, recently used) or Calendar (a
// custom range), each with the query date field. A bonus period gadget picks
// its bonus period instead.
const DatePickerPanel = ({
  isBonusPeriodMode,
  onClosed,
}: {
  isBonusPeriodMode?: boolean;
  onClosed: () => void;
}) => {
  const initialTab = useInitialTab();
  const [tab, setTab] = useState<Tab>(initialTab);
  const leftText = useLeftText();
  const rightText = useRightText();
  // The dialog follows the pane's size itself (autoUpdate)
  const updateDialogLayout = useCallback(() => {}, []);

  if (isBonusPeriodMode) {
    return (
      <BonusPeriod data-testid="date-picker-panel">
        <PeriodSelector isWorkSpaceTopBar={false} isBonusPeriodMode />
      </BonusPeriod>
    );
  }

  return (
    <Wrapper data-testid="date-picker-panel">
      <Header>
        <div data-testid="date-picker-panel-range">
          <Typography.Body type="Body 13 Bold" isEllipsis>
            {`${leftText} · ${rightText}`}
          </Typography.Body>
        </div>
        <Tabs role="tablist">
          {TABS.map((t) => (
            <TabButton
              key={t.key}
              type="button"
              role="tab"
              aria-selected={tab === t.key}
              $isSelected={tab === t.key}
              onClick={() => setTab(t.key)}
              data-testid={`date-picker-tab-${t.key}`}
            >
              <Typography.Body
                type="Body 12"
                color={tab === t.key ? undefined : 'var(--color-text-tertiary)'}
              >
                {t.label}
              </Typography.Body>
            </TabButton>
          ))}
        </Tabs>
      </Header>
      <Body data-testid="date-picker-panel-body">
        {tab === 'relative' ? (
          <RelativeInput updateDialogLayout={updateDialogLayout} />
        ) : (
          <CalendarInput onClosed={onClosed} />
        )}
      </Body>
    </Wrapper>
  );
};

export default DatePickerPanel;
